import React, { useState, useMemo, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProductTable = ({ 
  products, 
  quantities, 
  onQuantityChange, 
  searchQuery,
  onScrollToTop,
  productFlags = {},
  onProductFlagChange
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // ✅ BUGFIX: Reset page to 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, products?.length]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!sortConfig?.key) return products;

    return [...products]?.sort((a, b) => {
      const aVal = a?.[sortConfig?.key] || '';
      const bVal = b?.[sortConfig?.key] || '';
      
      // Handle numeric sorting for price
      if (sortConfig?.key === 'price' || sortConfig?.key === 'publicPrice' || sortConfig?.key === 'resellerPrice') {
        const aPrice = parseFloat(aVal?.toString()?.replace(',', '.')) || 0;
        const bPrice = parseFloat(bVal?.toString()?.replace(',', '.')) || 0;
        return sortConfig?.direction === 'asc' ? aPrice - bPrice : bPrice - aPrice;
      }
      
      // String sorting
      const comparison = aVal?.toString()?.localeCompare(bVal?.toString(), 'it', { numeric: true });
      return sortConfig?.direction === 'asc' ? comparison : -comparison;
    });
  }, [products, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedProducts?.length / itemsPerPage);
  const paginatedProducts = sortedProducts?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleQuantityChange = (sku, value) => {
    const quantity = Math.max(0, parseInt(value) || 0);
    onQuantityChange?.(sku, quantity);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    onScrollToTop?.();
  };

  const handleFlagChange = (sku, isChecked) => {
    onProductFlagChange?.(sku, isChecked);
  };

  const getSortIcon = (key) => {
    if (sortConfig?.key !== key) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const highlightText = (text, query) => {
    if (!query?.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text?.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
  };

  if (!products?.length) {
    return (
      <div className="bg-card rounded-lg border border-border p-8 text-center">
        <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Nessun Prodotto Disponibile</h3>
        <p className="text-muted-foreground">
          Non sono stati trovati prodotti nel listino
        </p>
      </div>
    );
  }

  // ✅ BUGFIX: Show message if on empty page due to pagination
  if (paginatedProducts?.length === 0 && sortedProducts?.length > 0) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">
              Listino Prodotti
            </h3>
            <span className="text-sm text-muted-foreground">
              {sortedProducts?.length} prodotti trovati
            </span>
          </div>
        </div>
        
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Pagina Vuota</h3>
          <p className="text-muted-foreground mb-4">
            Hai {sortedProducts?.length} risultati ma stai guardando una pagina vuota.
          </p>
          <Button
            variant="default"
            onClick={() => handlePageChange(1)}
            iconName="ArrowLeft"
            iconPosition="left"
          >
            Vai alla Prima Pagina
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/20">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-foreground">
            Listino Prodotti
          </h3>
          <span className="text-sm text-muted-foreground">
            {sortedProducts?.length} prodotti {searchQuery?.trim() ? 'trovati' : 'disponibili'}
          </span>
        </div>
      </div>

      {/* ✅ IMPROVEMENT: Show current page info when searching */}
      {searchQuery?.trim() && totalPages > 1 && (
        <div className="px-4 py-2 bg-blue-50 border-b border-border">
          <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
            <span className="text-blue-700">
              🔍 Risultati per "{searchQuery}" - Pagina {currentPage} di {totalPages}
            </span>
            {currentPage > 1 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                className="text-blue-600 border-blue-200"
              >
                Prima Pagina
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Mobile Cards */}
      <div className="space-y-3 p-3 md:hidden">
        {paginatedProducts?.map((product) => (
          <div key={product?.sku} className="rounded-xl border border-border bg-white p-4 shadow-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={productFlags?.[product?.sku] || false}
                  onChange={(e) => handleFlagChange(product?.sku, e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 cursor-pointer"
                  title="Contrassegna questo prodotto"
                />
                <span
                  className="font-mono text-xs text-muted-foreground break-all"
                  dangerouslySetInnerHTML={{
                    __html: highlightText(product?.sku || '', searchQuery)
                  }}
                />
              </div>
              <div
                className="mt-2 text-sm font-semibold text-foreground break-words"
                dangerouslySetInnerHTML={{
                  __html: highlightText(product?.name || '', searchQuery)
                }}
              />
              {product?.description && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-3 break-words">
                  {product?.description}
                </p>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-muted/30 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Prezzo pubb.</p>
                {product?.publicPrice && parseFloat(product?.publicPrice?.toString()?.replace(',', '.')) > 0 ? (
                  <span className="mt-1 block font-semibold text-foreground">
                    €{parseFloat(product?.publicPrice?.toString()?.replace(',', '.'))?.toFixed(2)}
                  </span>
                ) : (
                  <span className="mt-1 block text-xs text-muted-foreground">Su richiesta</span>
                )}
              </div>
              <div className="rounded-lg bg-green-50 p-3">
                <p className="text-[11px] uppercase tracking-wide text-green-700">Reseller</p>
                {product?.resellerPrice && parseFloat(product?.resellerPrice?.toString()?.replace(',', '.')) > 0 ? (
                  <span className="mt-1 block font-semibold text-green-700">
                    €{parseFloat(product?.resellerPrice?.toString()?.replace(',', '.'))?.toFixed(2)}
                  </span>
                ) : (
                  <span className="mt-1 block text-xs text-muted-foreground">Su richiesta</span>
                )}
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/20 p-2">
              <span className="text-sm font-medium text-foreground">Quantità</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(product?.sku, (quantities?.[product?.sku] || 0) - 1)}
                  disabled={(quantities?.[product?.sku] || 0) <= 0}
                  className="h-9 w-9 p-0"
                >
                  <Icon name="Minus" size={12} />
                </Button>
                <Input
                  type="number"
                  min="0"
                  max="999"
                  value={quantities?.[product?.sku] || 0}
                  onChange={(e) => handleQuantityChange(product?.sku, e?.target?.value)}
                  className="w-16 text-center text-sm"
                  size="sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuantityChange(product?.sku, (quantities?.[product?.sku] || 0) + 1)}
                  className="h-9 w-9 p-0"
                >
                  <Icon name="Plus" size={12} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          <thead className="bg-muted/10">
            <tr>
              <th className="text-center p-3 font-medium text-foreground w-16">
                F
              </th>
              <th className="text-left p-3 font-medium text-foreground">
                <button 
                  onClick={() => handleSort('sku')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>SKU</span>
                  <Icon name={getSortIcon('sku')} size={14} />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-foreground">
                <button 
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Nome</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-foreground">
                <button 
                  onClick={() => handleSort('publicPrice')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Prezzo pubb. iva esc.</span>
                  <Icon name={getSortIcon('publicPrice')} size={14} />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-foreground">
                <button 
                  onClick={() => handleSort('resellerPrice')}
                  className="flex items-center space-x-1 hover:text-primary transition-colors"
                >
                  <span>Reseller Price iva esc.</span>
                  <Icon name={getSortIcon('resellerPrice')} size={14} />
                </button>
              </th>
              <th className="text-center p-3 font-medium text-foreground w-32">
                Quantità
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts?.map((product, index) => (
              <tr 
                key={product?.sku} 
                className={`border-b border-border hover:bg-muted/20 transition-colors
                  ${index % 2 === 0 ? 'bg-muted/5' : ''}`}
              >
                <td className="p-3 text-center">
                  <div className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={productFlags?.[product?.sku] || false}
                      onChange={(e) => handleFlagChange(product?.sku, e?.target?.checked)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 cursor-pointer"
                      title="Contrassegna questo prodotto"
                    />
                  </div>
                </td>
                <td className="p-3">
                  <span 
                    className="font-mono text-sm text-foreground"
                    dangerouslySetInnerHTML={{ 
                      __html: highlightText(product?.sku || '', searchQuery) 
                    }}
                  />
                </td>
                <td className="p-3">
                  <div>
                    <div 
                      className="font-medium text-foreground"
                      dangerouslySetInnerHTML={{ 
                        __html: highlightText(product?.name || '', searchQuery) 
                      }}
                    />
                    {product?.description && (
                      <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {product?.description}
                      </div>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  {product?.publicPrice && parseFloat(product?.publicPrice?.toString()?.replace(',', '.')) > 0 ? (
                    <span className="font-semibold text-foreground">
                      €{parseFloat(product?.publicPrice?.toString()?.replace(',', '.'))?.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">Su richiesta</span>
                  )}
                </td>
                <td className="p-3">
                  {product?.resellerPrice && parseFloat(product?.resellerPrice?.toString()?.replace(',', '.')) > 0 ? (
                    <span className="font-semibold text-green-600">
                      €{parseFloat(product?.resellerPrice?.toString()?.replace(',', '.'))?.toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">Su richiesta</span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuantityChange(product?.sku, (quantities?.[product?.sku] || 0) - 1)}
                      disabled={(quantities?.[product?.sku] || 0) <= 0}
                      className="w-8 h-8 p-0"
                    >
                      <Icon name="Minus" size={12} />
                    </Button>
                    <Input
                      type="number"
                      min="0"
                      max="999"
                      value={quantities?.[product?.sku] || 0}
                      onChange={(e) => handleQuantityChange(product?.sku, e?.target?.value)}
                      className="w-16 text-center text-sm"
                      size="sm"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleQuantityChange(product?.sku, (quantities?.[product?.sku] || 0) + 1)}
                      className="w-8 h-8 p-0"
                    >
                      <Icon name="Plus" size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-border bg-muted/10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-muted-foreground">
              Pagina {currentPage} di {totalPages} 
              ({sortedProducts?.length} prodotti {searchQuery?.trim() ? 'trovati' : 'totali'})
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <Icon name="ChevronLeft" size={14} />
              </Button>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + Math.max(1, currentPage - 2);
                if (page > totalPages) return null;
                
                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className="w-8"
                  >
                    {page}
                  </Button>
                );
              })}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                <Icon name="ChevronRight" size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductTable;
